import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ErrorBanner, RequiredMark, Skeleton, useConfirm, useModalA11y, useToast } from "./ui-kit";

type AgentToken = { createdAt: string; id: string; label: string; lastUsedAt: string | null; revokedAt: string | null };

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

/**
 * Self-serve personal-access-token management for MCP/agent access (see
 * src/mcp-server.ts). Any signed-in user manages only their own tokens --
 * there's no admin view of other people's tokens, since a token can only
 * ever act as its own creator.
 */
export function AgentTokensDialog({ onClose }: { onClose: () => void }) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [tokens, setTokens] = useState<AgentToken[] | null>(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const showToast = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const result = await api<{ tokens: AgentToken[] }>("/api/agent-tokens");
    setTokens(result.tokens.filter((token) => !token.revokedAt));
  }, []);

  useEffect(() => {
    void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load agent tokens."));
  }, [load]);

  const generate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const result = await api<{ token: string }>("/api/agent-tokens", { body: JSON.stringify({ label: newLabel }), method: "POST" });
      setMintedToken(result.token);
      setNewLabel(""); setCreating(false);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not generate a token.");
    } finally { setBusy(false); }
  };

  const revoke = async (token: AgentToken) => {
    const confirmed = await confirm({
      confirmLabel: "Revoke token",
      description: `"${token.label}" will stop working immediately for any agent using it. This cannot be undone.`,
      title: `Revoke ${token.label}?`,
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await api(`/api/agent-tokens/${token.id}`, { method: "DELETE" });
      showToast("Token revoked.", "success");
      await load();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not revoke that token.", "error");
    }
  };

  const copyToken = async () => {
    if (!mintedToken) return;
    await navigator.clipboard.writeText(mintedToken);
    setCopied(true);
    showToast("Copied to clipboard.", "success");
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-modal="true" className="dialog agent-tokens-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">Agent access</p>
        <h2>Personal access tokens</h2>
        <p className="section-description">
          Generate a token to let Claude or another MCP-compatible agent act on your behalf. An agent using your token
          can never do anything you couldn't already do yourself in Taskei.
        </p>
        <ErrorBanner>{error}</ErrorBanner>

        {mintedToken && (
          <div className="agent-token-reveal">
            <p><strong>Copy this token now</strong> — it will not be shown again.</p>
            <div className="agent-token-value">
              <code>{mintedToken}</code>
              <button className="button secondary" onClick={() => void copyToken()} type="button">{copied ? "Copied" : "Copy"}</button>
            </div>
            <button className="button primary" onClick={() => setMintedToken(null)} type="button">Done</button>
          </div>
        )}

        {!mintedToken && (
          !tokens ? <Skeleton label="Loading tokens…" variant="list" /> : (
            <>
              {tokens.length ? (
                <ul className="agent-token-list">
                  {tokens.map((token) => (
                    <li key={token.id}>
                      <div>
                        <strong>{token.label}</strong>
                        <p>
                          Created {new Date(token.createdAt).toLocaleDateString()}
                          {token.lastUsedAt ? ` · last used ${new Date(token.lastUsedAt).toLocaleDateString()}` : " · never used"}
                        </p>
                      </div>
                      <button className="button danger" onClick={() => void revoke(token)} type="button">Revoke</button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state compact">
                  <h2>No agent tokens yet</h2>
                  <p>Generate one to connect Claude or another MCP client to Taskei.</p>
                </div>
              )}
              {creating ? (
                <form className="agent-token-create-form" onSubmit={generate}>
                  <label>
                    <span>Label<RequiredMark /></span>
                    <input autoFocus onChange={(event) => setNewLabel(event.target.value)} placeholder="e.g. Claude Desktop" required value={newLabel} />
                  </label>
                  <div className="dialog-actions">
                    <button className="button secondary" onClick={() => setCreating(false)} type="button">Cancel</button>
                    <button className="button primary" disabled={busy} type="submit">Generate token</button>
                  </div>
                </form>
              ) : (
                <button className="button primary" onClick={() => setCreating(true)} type="button">+ Generate token</button>
              )}
            </>
          )
        )}
      </section>
    </div>
  );
}
